import find from 'lodash/find'
import map from 'lodash/map'
import { useRouter } from 'next/router'
import React, { FC, memo } from 'react'

import { SidepaneAccordion } from '@Components/CMS/AccordionSidePane'
import { DescriptionHeader, DescriptionTextWrapper, MobileOnlyWrapper } from '@Components/Product/Display/styledElements'
import { BaseAttribute, Product } from '@Generated/graphql'
import { useTranslation } from '@Globals/Translations'

export interface GenericAttributeRendererProps {
  asSection?: boolean
  attributeKeyName: string
  header: string
  includeHeader?: boolean
  product?: Product
  selectedVariantID?: string
}

const RendererWrapper: FC<Pick<GenericAttributeRendererProps, 'header'>> = memo(({ children, header }) => {
  const { t } = useTranslation()

  return (
    <MobileOnlyWrapper>
      <SidepaneAccordion color="white" header={t(header)}>
        <DescriptionTextWrapper>
          {children}
        </DescriptionTextWrapper>
      </SidepaneAccordion>
    </MobileOnlyWrapper>
  )
})

export const GenericTextAttributeRenderer: FC<GenericAttributeRendererProps> = memo(({
  asSection,
  attributeKeyName,
  children,
  header,
  includeHeader,
  product,
  selectedVariantID,
}) => {
  const { locale = 'en-GB' } = useRouter()
  const { t } = useTranslation()
  const currentVariantData = (selectedVariantID && find(product?.variants, ['id', selectedVariantID])) || product?.masterVariant
  const textArray = ((find(currentVariantData?.attributes, ['key', attributeKeyName]) as BaseAttribute)?.value || [])

  const localizedText = textArray[locale] || ''

  if (!localizedText) {
    return null
  }

  const content = (
    <div>
      {includeHeader && <DescriptionHeader>{t(header)}</DescriptionHeader>}
      {map(
        localizedText.trim().split('\n'),
        (line, index) => {
          const trimmedData = line.trim()
          return trimmedData.length
            ? <div key={`${index}${line}`}>{trimmedData}</div>
            : null
        },
      )}
      {children}
    </div>
  )

  return asSection ? <RendererWrapper header={header}>{content}</RendererWrapper> : content
})
